import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import { styled } from '@mui/material/styles';
import { useStacklineTheme, StacklineTheme, hexToRgba, SlDropdownMenuOption } from '@stackline/ui';
import { Divider } from '@mui/material';
import { CloseIcon } from 'src/components/SvgIcons/SvgIcons';
import { useExportContext } from 'src/providers/ExportContextProvider';
import { Link } from 'react-router-dom';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import {
  BeaconTopicExportType,
  TopicExportRequestBuilder
} from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils/TopicExportRequestBuilder';
import { topicSettingsServerProxy } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/serverProxy';
import { ExportCheckBox } from 'src/components/BeaconV2Components/common/AppExportModal/AppExportModal';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import { useTopicExportModalStore } from 'src/zustandStores/BeaconPro/Topics/Exports/TopicExportModalStore';
import { useMainEntity, useMainEntityType, useRetailerDisplayName } from 'src/utils/Hooks/reduxSelectors';
import { BEACON_SUBTABS, BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { useBaseTopicRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils';

interface GenericStyledDialogueProps {
  theme: StacklineTheme;
}

const GenericStyledDialogue = styled(Dialog)<GenericStyledDialogueProps>(({ theme }) => ({
  '& .MuiPaper-root': {
    width: '640px',
    height: '545px',
    boxShadow: `0 0 ${theme.spacing.md} 0 ${hexToRgba(theme.colors.primary, 0.4)}`,
    borderRadius: theme.spacing.sm
  }
}));

export const TOPIC_EXPORT_TYPES: { [key: string]: string } = {
  PRODUCT: 'GroupByProductTopics',
  BRAND: 'GroupByBrandTopics',
  CATEGORY: 'GroupByCategoryTopics',
  SUBCATEGORY: 'GroupBySubcategoryTopics',
  BRAND_SUBCATEGORY: 'GroupByBrandSubcategoryTopics',
  KEYWORD: 'GroupByKeywordTopics',
  BRAND_KEYWORD: 'GroupByBrandKeywordTopics',
  CAMPAIGN_KEYWORD: 'GroupByCampaignKeywordTopics',
  PRODUCT_KEYWORD: 'GroupByProductKeywordTopics',
  // Special types for paid traffic
  KEYWORD_PAID_TRAFFIC_TOPIC: 'GroupByKeywordTopicsPaidTraffic',
  KEYWORD_PAID_TRAFFIC_TOPIC_GROUP: 'GroupByKeywordTopicGroupsPaidTraffic',
  KEYWORD_BY_WEEK_PAID_TRAFFIC_TOPIC: 'GroupByKeywordByWeekTopicsPaidTraffic',
  KEYWORD_BY_WEEK_PAID_TRAFFIC_TOPIC_GROUP: 'GroupByKeywordByWeekTopicGroupsPaidTraffic'
};

export const TOPIC_GROUP_EXPORT_TYPES: { [key: string]: string } = {
  PRODUCT: 'GroupByProductTopicGroups',
  BRAND: 'GroupByBrandTopicGroups',
  CATEGORY: 'GroupByCategoryTopicGroups',
  SUBCATEGORY: 'GroupBySubcategoryTopicGroups',
  BRAND_SUBCATEGORY: 'GroupByBrandSubcategoryTopicGroups',
  KEYWORD: 'GroupByKeywordTopicGroups',
  BRAND_KEYWORD: 'GroupByBrandKeywordTopicGroups',
  CAMPAIGN_KEYWORD: 'GroupByCampaignKeywordTopicGroups',
  PRODUCT_KEYWORD: 'GroupByProductKeywordTopicGroups',
  // Special types for paid traffic
  KEYWORD_PAID_TRAFFIC_TOPIC: 'GroupByKeywordTopicGroupsPaidTraffic',
  KEYWORD_PAID_TRAFFIC_TOPIC_GROUP: 'GroupByKeywordTopicGroupsPaidTraffic',
  KEYWORD_BY_WEEK_PAID_TRAFFIC_TOPIC: 'GroupByKeywordByWeekTopicGroupsPaidTraffic',
  KEYWORD_BY_WEEK_PAID_TRAFFIC_TOPIC_GROUP: 'GroupByKeywordByWeekTopicGroupsPaidTraffic'
};

const getDropdownOptions = (isTopicGroup: boolean) => {
  const EXPORT_TYPES = isTopicGroup ? TOPIC_GROUP_EXPORT_TYPES : TOPIC_EXPORT_TYPES;
  return [
    { id: EXPORT_TYPES.CATEGORY, label: 'Category' },
    { id: EXPORT_TYPES.BRAND, label: 'Brand' },
    { id: EXPORT_TYPES.SUBCATEGORY, label: 'Subcategory' },
    { id: EXPORT_TYPES.PRODUCT, label: 'Product' },
    { id: EXPORT_TYPES.BRAND_SUBCATEGORY, label: 'Brand & Subcategory' },
    { id: EXPORT_TYPES.KEYWORD, label: 'Keyword' },
    { id: EXPORT_TYPES.BRAND_KEYWORD, label: 'Brand & Keyword' },
    { id: EXPORT_TYPES.CAMPAIGN_KEYWORD, label: 'Campaign & Keyword' },
    { id: EXPORT_TYPES.PRODUCT_KEYWORD, label: 'Product & Keyword' }
  ];
};

/**
 * Builds a string based on the application conditions to be used as the export file name
 * @example "client_Starbucks_traffic_totalclicks_organicclicks_202101_202101"
 */
export const useExportFileName = () => {
  const type = useMainEntityType();
  const name = useMainEntity()?.name;
  const { tab, subtab, sw, ew } = useAppSelector((state) => state.app.queryParams);
  const retailer = useRetailerDisplayName();
  const tabLabel = subtab && subtab !== 'keymetrics' ? subtab : tab;
  const raw = `${name}_${tabLabel}_${retailer}_${sw}_${ew}`;
  const sanitized = raw.replace(/Amazon/g, 'Amz').replace(/_+/g, '_');
  const suffix = `_${sw}_${ew}`;
  return sanitized.length > 150 ? `${sanitized.slice(0, 150 - suffix.length)}${suffix}` : sanitized;
};

/**
 * TODO: Add separate by week property to request
 */
export const TopicExportModal = () => {
  const theme = useStacklineTheme();
  const { isOpen, closeModal } = useTopicExportModalStore();

  const baseRequest = useBaseTopicRequestBuilder(true) as TopicExportRequestBuilder;
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);
  const queryParams = useAppSelector((state) => state.app.queryParams);
  const tab = queryParams?.tab;
  const subtab = queryParams?.subtab;
  const { startWeek, endWeek } = mainTimePeriod;
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = comparisonTimePeriod;

  const fileName = useExportFileName();

  const mainEntity = useMainEntity();

  const isTopic = mainEntity?.type === 'topic';
  const isTopicGroup = mainEntity?.type === 'topicgroup';
  const EXPORT_TYPES = isTopicGroup ? TOPIC_GROUP_EXPORT_TYPES : TOPIC_EXPORT_TYPES;

  const [isIncludeComparison, setIsIncludeComparison] = useState(false);

  const [isSeparatedByWeek, setIsSeparatedByWeek] = useState(false);

  const OPTIONS = getDropdownOptions(isTopicGroup);
  const [selectedGroupBy, setSelectedGroupBy] = useState(OPTIONS[0]);

  useEffect(() => {
    setSelectedGroupBy(OPTIONS[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTopicGroup]);

  const disableWeekSeparation = [
    EXPORT_TYPES.BRAND_KEYWORD,
    EXPORT_TYPES.CAMPAIGN_KEYWORD,
    EXPORT_TYPES.PRODUCT_KEYWORD
  ].includes(selectedGroupBy.id);

  const [exportButtonDisabled, setExportButtonDisabled] = useState(false);
  const { startPolling } = useExportContext();
  const { showSnackbar } = useSnackbar();

  const handleClose = () => {
    setIsIncludeComparison(false);
    setIsSeparatedByWeek(false);
    setSelectedGroupBy(OPTIONS[0]);
    closeModal();
  };

  const handleExportClick = async () => {
    try {
      // Special logic for paid traffic export
      const isPaidTrafficExport =
        tab === BEACON_TABS.TRAFFIC &&
        subtab === BEACON_SUBTABS.AD_CLICKS &&
        selectedGroupBy.id === EXPORT_TYPES.KEYWORD;

      const sortByTotalTraffic = subtab === BEACON_SUBTABS.TOTAL_CLICKS;
      const sortByOrganicTraffic = subtab === BEACON_SUBTABS.ORGANIC_CLICKS;

      baseRequest.setStartWeekId(startWeek);
      baseRequest.setEndWeekId(endWeek);

      if (isTopic) {
        baseRequest.setTopicIds([`${mainEntity?.id}`]);
      } else if (isTopicGroup) {
        baseRequest.setTopicGroupIds([`${mainEntity?.id}`]);
      }

      baseRequest.setFileName(fileName);

      if (isSeparatedByWeek) {
        baseRequest.setShouldGroupByWeek(true);
      }

      if (isIncludeComparison) {
        baseRequest.setComparisonStartWeekId(comparisonStartWeek);
        baseRequest.setComparisonEndWeekId(comparisonEndWeek);
      }

      if (isPaidTrafficExport) {
        const exportType = isSeparatedByWeek
          ? isTopic
            ? EXPORT_TYPES.KEYWORD_BY_WEEK_PAID_TRAFFIC_TOPIC
            : EXPORT_TYPES.KEYWORD_BY_WEEK_PAID_TRAFFIC_TOPIC_GROUP
          : isTopic
            ? EXPORT_TYPES.KEYWORD_PAID_TRAFFIC_TOPIC
            : EXPORT_TYPES.KEYWORD_PAID_TRAFFIC_TOPIC_GROUP;

        if (isSeparatedByWeek) {
          baseRequest.setShouldGroupByWeek(false); // Throws an error if set to true, we use the exportType to determine if we should group by week for paid traffic
        }

        baseRequest.setExportType(exportType as BeaconTopicExportType);
      } else {
        baseRequest.setExportType(selectedGroupBy?.id as BeaconTopicExportType);
      }

      if (sortByTotalTraffic) {
        baseRequest.setSortField('totalTraffic');
      }
      if (sortByOrganicTraffic) {
        baseRequest.setSortField('organicTraffic');
      }

      const request = baseRequest.build();

      setExportButtonDisabled(true);
      await topicSettingsServerProxy.exportTopic(request);
      startPolling();
      setExportButtonDisabled(false);
      handleClose();
      showSnackbar({ type: 'success', message: 'Your download is processing.' });
    } catch (err) {
      setExportButtonDisabled(false);
      handleClose();
      showSnackbar({
        message: <GenericErrorMessage />,
        type: 'error'
      });

      console.error('Error while exporting Topic data: ', err);
    }
  };

  const handleDropdownChange = (option: SlDropdownMenuOption) => {
    setSelectedGroupBy(option);
  };

  return (
    <GenericStyledDialogue open={isOpen} onClose={handleClose} theme={theme}>
      {/* Modal Header */}
      <>
        <Flex height="72px" justifyContent="center" alignItems="center" paddingX="48px" paddingY="18px">
          <Text variant="h4">Export</Text>
          <CloseIcon
            onClick={handleClose}
            style={{ position: 'absolute', cursor: 'pointer', width: '24px', height: '24px', right: '24px' }}
          ></CloseIcon>
        </Flex>
        <Divider />
      </>

      <Flex flexDirection="column" paddingTop="23.5px" marginLeft="48px" height="400px">
        {/* MODAL DESCRIPTION */}
        <Text variant="subtitle1" sx={{ height: '21px' }}>
          Export Data Structure
        </Text>
        <Flex marginTop="8px">
          <Text variant="body2">Select the level of granularity that you would like in the exported file.</Text>
        </Flex>

        {/* GROUP BY DROPDOWN */}
        <Flex marginTop="24px" alignItems="center">
          <Text sx={{ height: '19px' }} variant="subtitle2">
            Group by:
          </Text>
          <Flex marginLeft="8px">
            <SlDropdownMenu
              options={OPTIONS}
              selectedId={selectedGroupBy?.id}
              onChange={(option) => handleDropdownChange(option)}
              stacklineButtonProps={{
                buttonSx: {
                  minWidth: '99px',
                  justifyContent: 'space-between'
                }
              }}
            />
          </Flex>
        </Flex>

        {/* CUSTOMIZED EXPORT OPTIONS */}
        <>
          <Flex marginTop="24px">
            <Text sx={{ height: '19px' }} variant="subtitle2">
              Customize
            </Text>
          </Flex>
          <Flex flexDirection="column" gap={3} marginTop="6.5px">
            <ExportCheckBox
              isSelected={isSeparatedByWeek}
              id="separateDataByWeek"
              label="Separate data by week"
              onSelect={(e) => setIsSeparatedByWeek(e.target.checked)}
              checkboxProps={{
                checked: isSeparatedByWeek,
                disabled: disableWeekSeparation
              }}
            />
            {/* Include comparison checkbox */}
            <ExportCheckBox
              isSelected={isIncludeComparison}
              id="includeComparison"
              label="Include comparison time period"
              onSelect={(e) => setIsIncludeComparison(e.target.checked)}
            />
          </Flex>
        </>
      </Flex>
      {/* Footer */}
      <>
        <Divider />
        <Flex justifyContent="space-between" alignItems="center" paddingX="48px" paddingY="20px">
          <Link to={`/account/downloads${queryParams.searchParams}`}>
            <SlButton sx={{ width: '142px' }} onClick={handleClose}>
              View downloads
            </SlButton>
          </Link>
          <SlButton
            buttonSx={{ width: '81px' }}
            variant="contained"
            onClick={handleExportClick}
            disabled={exportButtonDisabled}
          >
            Export
          </SlButton>
        </Flex>
      </>
    </GenericStyledDialogue>
  );
};
